import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { API, fmtBytes, fmtDate, fmtDuration } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, RotateCcw, XCircle, Download, FileJson } from "lucide-react";

export default function RunDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [logs, setLogs] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/runs/${id}`);
      setRun(r.data);
      const l = await api.get(`/runs/${id}/logs`, { responseType: "text" });
      setLogs(typeof l.data === "string" ? l.data : JSON.stringify(l.data));
    } catch { toast.error(t("runDetail.loadFailed")); }
  }, [id, t]);

  useEffect(() => {
    load();
    const tt = setInterval(() => {
      setRun((cur) => {
        if (cur && ["queued", "running"].includes(cur.status)) load();
        return cur;
      });
    }, 4000);
    return () => clearInterval(tt);
  }, [load]);

  const retry = async () => {
    try { const r = await api.post(`/runs/${id}/retry`); toast.success(t("runDetail.retryQueued")); navigate(`/runs/${r.data.run_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || t("runDetail.failed")); }
  };
  const cancel = async () => {
    try { await api.post(`/runs/${id}/cancel`); toast.success(t("runDetail.cancelRequested")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("runDetail.failed")); }
  };

  if (!run) return <div className="p-8 font-mono text-sm text-muted-foreground spinner-dots">{t("common.loading")}</div>;
  const active = ["queued", "running"].includes(run.status);
  const isPhysical = run.engine === "physical";
  const downloadUrl = (file) => `${API}/runs/${id}/download?file=${encodeURIComponent(file)}`;
  const summary = [
    [t("runDetail.phase"), run.phase || "—"],
    [t("runDetail.engine"), isPhysical ? `physical · ${run.execution_mode || ""}` : t("runDetail.engineLogical")],
    ...(isPhysical ? [[t("runDetail.backupType"), run.backup_type || "full"], [t("runDetail.artifactFormat"), run.artifact_format || "—"],
                     [t("runDetail.prepared"), run.prepared ? t("runDetail.yes") : t("runDetail.no")], [t("runDetail.remoteCleanup"), run.remote_cleanup_status || "—"]] : []),
    [t("runDetail.duration"), fmtDuration(run.duration_seconds)],
    [t("runDetail.artifactSize"), fmtBytes(run.final_size)],
    [t("runDetail.compression"), run.compression_algorithm ? `${run.compression_algorithm} ${run.compression_level || ""}` : "—"],
    [t("runDetail.encryption"), run.encryption_algorithm || t("runDetail.encryptionNone")],
    [t("runDetail.serverVersion"), run.server_version || "—"],
    [t("runDetail.started"), fmtDate(run.started_at)],
    [t("runDetail.finished"), fmtDate(run.finished_at)],
    ...(isPhysical
      ? [["LSN", run.to_lsn ? `${run.from_lsn ?? 0} → ${run.to_lsn}` : "—"],
         ["Binlog", run.binlog_file ? `${run.binlog_file}:${run.binlog_position}` : "—"],
         [t("runDetail.chain"), run.chain_id ? `${run.chain_id.slice(0, 12)}…` : "—"]]
      : [[t("runDetail.binlogCoord"), run.binlog_coordinate?.file ? `${run.binlog_coordinate.file}:${run.binlog_coordinate.position}` : "—"]]),
  ];

  return (
    <div data-testid="run-detail-page">
      <PageHeader title={t("runDetail.runTitle", { id: run.id.slice(0, 12) })} subtitle={run.plan_name} badge={<StatusBadge status={run.status} />}
        actions={
          <>
            <Button variant="outline" className="rounded-none" onClick={() => navigate("/runs")}><ArrowLeft className="h-4 w-4 mr-1" />{t("common.back")}</Button>
            {active
              ? <Button variant="outline" className="rounded-none uppercase text-destructive" onClick={cancel} data-testid="cancel-run-button"><XCircle className="h-4 w-4 mr-1.5" />{t("runDetail.cancel")}</Button>
              : <Button className="rounded-none uppercase bg-[#0A0A0A] hover:bg-[#0052FF]" onClick={retry} data-testid="retry-run-button"><RotateCcw className="h-4 w-4 mr-1.5" />{t("runDetail.retry")}</Button>}
          </>
        } />
      <div className="p-6 md:p-8 space-y-6">
        {/* Progress / summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border grid-panel">
          {summary.map(([k, v]) => (
            <div key={k} className="bg-card px-5 py-3">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{k}</p>
              <p className="text-sm font-medium mt-1 truncate" title={String(v)}>{v}</p>
            </div>
          ))}
        </div>

        {active && run.progress && (
          <div className="grid-panel p-5" data-testid="run-progress">
            <div className="flex items-center justify-between mb-2">
              <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                {isPhysical ? `SSH · ${run.progress.phase || run.phase}` : (run.phase === "dumping" ? t("runDetail.dumping", { db: run.progress.current_db || "…" }) : run.phase)}
              </p>
              <p className="font-mono text-xs">{fmtBytes(run.progress.bytes_written)} {isPhysical ? t("runDetail.streamed") : t("runDetail.written")} · {fmtDuration(run.progress.elapsed)}</p>
            </div>
            <div className="h-1.5 w-full bg-secondary overflow-hidden">
              <div className="h-full bg-brand animate-pulse" style={{ width: "100%" }} />
            </div>
          </div>
        )}

        {run.error_message && (
          <div className="grid-panel border-destructive p-5" data-testid="run-error">
            <p className="font-mono text-[11px] uppercase tracking-wide text-destructive mb-1">{t("runDetail.error")} · {run.error_category}</p>
            <p className="text-sm text-destructive">{run.error_message}</p>
          </div>
        )}

        {/* Destinations */}
        <div className="grid-panel">
          <div className="px-5 py-3 border-b border-border"><h3 className="font-display font-bold">{t("runDetail.destinations")}</h3></div>
          {run.destination_results?.length ? run.destination_results.map((d) => (
            <div key={d.destination_id} className="px-5 py-3 border-b border-border last:border-0 flex items-center justify-between" data-testid={`dest-result-${d.destination_id}`}>
              <div>
                <p className="font-medium text-sm">{d.destination_name || d.destination_id}</p>
                <p className="font-mono text-xs text-muted-foreground">{t("runDetail.filesCount", { n: d.uploaded_keys?.length || 0 })}{d.error ? ` · ${d.error}` : ""}</p>
              </div>
              <StatusBadge status={d.status} />
            </div>
          )) : <div className="px-5 py-6 text-sm text-muted-foreground">{t("runDetail.noDestUploads")}</div>}
        </div>

        {/* Files */}
        {run.files?.length > 0 && (
          <div className="grid-panel">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h3 className="font-display font-bold">{t("runDetail.artifacts")}</h3>
              <div className="flex gap-2">
                <a href={`${API}/runs/${id}/manifest`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="rounded-none" data-testid="download-manifest"><FileJson className="h-3.5 w-3.5 mr-1" />{t("runDetail.manifest")}</Button>
                </a>
                <a href={`${API}/runs/${id}/checksum`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="rounded-none" data-testid="download-checksum">SHA256</Button>
                </a>
              </div>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead><tr className="text-left font-mono text-[10px] uppercase text-muted-foreground border-b border-border"><th className="px-5 py-2">{t("runDetail.thFile")}</th><th className="px-5 py-2">{t("runDetail.thSize")}</th><th className="px-5 py-2">SHA256</th><th className="px-5 py-2"></th></tr></thead>
              <tbody>
                {run.files.map((f) => (
                  <tr key={f.name} className="data-grid-row">
                    <td className="px-5 py-2 font-mono text-xs">{f.name}</td>
                    <td className="px-5 py-2 font-mono text-xs">{fmtBytes(f.size)}</td>
                    <td className="px-5 py-2 font-mono text-[10px] text-muted-foreground">{f.sha256?.slice(0, 24)}…</td>
                    <td className="px-5 py-2 text-right">
                      <a href={downloadUrl(f.name)} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="ghost" className="rounded-none h-7" data-testid={`download-${f.name}`}><Download className="h-3.5 w-3.5" /></Button>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Logs */}
        <div className="grid-panel">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h3 className="font-display font-bold">{t("runDetail.redactedLog")}</h3>
            {run.status === "succeeded" && <Link to="/restore" className="font-mono text-xs text-brand hover:underline">{t("runDetail.restoreThis")}</Link>}
          </div>
          <pre className="p-5 font-mono text-xs leading-relaxed bg-[#0A0A0A] text-green-400 overflow-x-auto max-h-96" data-testid="run-logs">{logs || t("runDetail.noLogs")}</pre>
        </div>
      </div>
    </div>
  );
}
